/**
 * referral_channels の React Query hooks。
 * SNS 広告 / QR / 紙媒体などの外部チャネルプリセット CRUD。
 */
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ReferralChannel } from '@goenhub/shared';

import { db } from '@/lib/firebase';
import { safeAddDoc, safeDeleteDoc, safeUpdateDoc } from '@/lib/safeWrite';

export interface ReferralChannelRecord extends ReferralChannel {
  id: string;
}

export function useReferralChannels() {
  return useQuery({
    queryKey: ['referral_channels'],
    queryFn: async (): Promise<ReferralChannelRecord[]> => {
      const snap = await getDocs(
        query(collection(db, 'referral_channels'), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ReferralChannel),
      }));
    },
  });
}

export function useCreateReferralChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<ReferralChannel> & { createdBy: string }) => {
      await safeAddDoc(collection(db, 'referral_channels'), {
        name: data.name ?? '',
        refTag: data.refTag ?? '',
        category: data.category ?? '',
        description: data.description ?? '',
        color: data.color ?? '',
        usageCount: 0,
        createdBy: data.createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['referral_channels'] }),
  });
}

export function useUpdateReferralChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; data: Partial<ReferralChannel> }) => {
      await safeUpdateDoc(doc(db, 'referral_channels', args.id), {
        ...args.data,
        updatedAt: serverTimestamp(),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['referral_channels'] }),
  });
}

export function useDeleteReferralChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await safeDeleteDoc(doc(db, 'referral_channels', id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['referral_channels'] }),
  });
}
